/**
 * Dropdown Menu Toggle Control
 * Handles dropdown menu show/hide state and visibility management
 */

import { Roles } from "../../constants/roles";
import { translator } from "../../translation/translator";
import { dropdownForm } from "./dropdown-form";

type DropdownMenuType = "post" | "comment";
type DropdownItemId = number;

class DropdownToggle {
    public adjustMenuPosition(menu: HTMLElement, button: HTMLElement | null | undefined): void {
        if (!button) {
            return;
        }

        menu.style.top = "";
        menu.style.bottom = "";
        menu.style.left = "";
        menu.style.right = "";

        const buttonRect = button.getBoundingClientRect();
        const menuRect = menu.getBoundingClientRect();
        const viewportHeight = document.documentElement.clientHeight;
        const viewportWidth = document.documentElement.clientWidth;
        const isPostMenu = menu.getAttribute("data-role") === Roles.POST_MENU;

        const menuBottom = menuRect.bottom;
        const spaceBelow = viewportHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;

        if (menuBottom > viewportHeight && spaceAbove > spaceBelow) {
            menu.style.top = "auto";
            menu.style.bottom = "100%";
            menu.style.marginBottom = "4px";
        } else {
            menu.style.top = "100%";
            menu.style.bottom = "auto";
            menu.style.marginTop = "4px";
        }

        const menuRight = menuRect.right;
        if (menuRight > viewportWidth) {
            menu.style.right = "0";
            menu.style.left = "auto";
        } else if (isPostMenu) {
            menu.style.right = "20px";
            menu.style.left = "auto";
        } else {
            menu.style.right = "0";
            menu.style.left = "auto";
        }

        const menuLeft = menuRect.left;
        if (menuLeft < 0) {
            menu.style.left = "0";
            menu.style.right = "auto";
        }
    }

    public toggleDropdownMenu(type: DropdownMenuType, itemId: DropdownItemId, isInModal = false): void {
        const searchContext = dropdownForm.getSearchContext(isInModal);
        if (!searchContext) {
            return;
        }

        const menu = dropdownForm.getMenuById(searchContext, type, itemId);

        if (!(menu instanceof HTMLElement)) {
            return;
        }

        const button = dropdownForm.getButtonById(searchContext, type, itemId);

        this.hideAllDropdownMenus();

        if (menu.style.display === "none" || menu.style.display === "") {
            menu.style.display = "block";
            translator.updateContent();

            requestAnimationFrame(() => {
                if (button instanceof HTMLElement) {
                    this.adjustMenuPosition(menu, button);
                }
            });
        } else {
            menu.style.display = "none";
        }
    }

    public hideAllDropdownMenus(): void {
        dropdownForm.getAllMenus().forEach((menu) => {
            menu.style.display = "none";
        });
    }

    public showDropdownMenu(type: DropdownMenuType, itemId: DropdownItemId, isInModal = false): void {
        const searchContext = dropdownForm.getSearchContext(isInModal);
        if (!searchContext) {
            return;
        }

        const menu = dropdownForm.getMenuById(searchContext, type, itemId);
        if (!(menu instanceof HTMLElement)) {
            return;
        }

        const button = dropdownForm.getButtonById(searchContext, type, itemId);

        this.hideAllDropdownMenus();
        menu.style.display = "block";
        translator.updateContent();

        requestAnimationFrame(() => {
            if (button instanceof HTMLElement) {
                this.adjustMenuPosition(menu, button);
            }
        });
    }

    public hideDropdownMenu(type: DropdownMenuType, itemId: DropdownItemId, isInModal = false): void {
        const searchContext = dropdownForm.getSearchContext(isInModal);
        if (!searchContext) {
            return;
        }

        const menu = dropdownForm.getMenuById(searchContext, type, itemId);
        if (!(menu instanceof HTMLElement)) {
            return;
        }

        menu.style.display = "none";
    }

    public isDropdownVisible(type: DropdownMenuType, itemId: DropdownItemId, isInModal = false): boolean {
        const searchContext = dropdownForm.getSearchContext(isInModal);
        if (!searchContext) {
            return false;
        }

        const menu = dropdownForm.getMenuById(searchContext, type, itemId);
        return menu instanceof HTMLElement && menu.style.display === "block";
    }

    public getVisibleDropdowns(): HTMLElement[] {
        return dropdownForm.getVisibleMenus();
    }
}

const dropdownToggle = new DropdownToggle();

export { dropdownToggle, DropdownToggle };
